import React from 'react'
import { Link } from '@inertiajs/react'
import {
  Box,
  Typography,
  Paper,
  Button,
  Chip,
} from '@mui/material'
import {
  Add as AddIcon,
  ChevronRight as ChevronRightIcon,
} from '@mui/icons-material'
import ManagementLayout from '@/components/layouts/ManagementLayout'
import EmptyState from '@/components/shared/EmptyState'
import type { Account } from '@/types'

interface Props {
  accounts: Account[]
}

export default function DashboardIndex({ accounts }: Props) {
  return (
    <ManagementLayout>
      <Box sx={{ maxWidth: 672, mx: 'auto' }}>
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Typography variant="h4" fontWeight="bold" gutterBottom>
            Welcome to S3 Proxy
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Select an account to continue
          </Typography>
        </Box>

        {accounts.length > 0 ? (
          <>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {accounts.map((account) => (
                <Paper
                  key={account.id}
                  component={Link}
                  href={`/accounts/${account.slug}/dashboard`}
                  sx={{
                    p: 3,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    textDecoration: 'none',
                    transition: 'all 0.15s ease-in-out',
                    border: '1px solid',
                    borderColor: 'grey.200',
                    '&:hover': {
                      borderColor: 'primary.main',
                      boxShadow: '0 0 0 1px #1a1a1a',
                    },
                  }}
                >
                  <Box>
                    <Typography variant="h6" fontWeight={600}>
                      {account.name}
                    </Typography>
                    <Box sx={{ mt: 1 }}>
                      <Chip
                        label={account.role === 'admin' ? 'Admin' : 'Member'}
                        size="small"
                        color={account.role === 'admin' ? 'primary' : 'default'}
                      />
                    </Box>
                  </Box>
                  <ChevronRightIcon sx={{ color: 'grey.400' }} />
                </Paper>
              ))}
            </Box>

            <Box sx={{ mt: 4, textAlign: 'center' }}>
              <Button
                component={Link}
                href="/accounts/new"
                startIcon={<AddIcon />}
                color="primary"
              >
                Create new account
              </Button>
            </Box>
          </>
        ) : (
          <EmptyState
            icon="inbox"
            title="No accounts"
            description="Get started by creating a new account."
            actionPath="/accounts/new"
            actionText="Create account"
          />
        )}
      </Box>
    </ManagementLayout>
  )
}
